/**
 * Put the next page at its top.
 *
 * Deliberately instant, not smooth: this runs as a route is changing, and a
 * smooth animation would still be travelling while the new page mounts, landing
 * the visitor part-way down content they have never seen. The second pass after
 * paint covers pages whose height settles once their content renders.
 */
export function jumpToTop(): void {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0 }))
}

interface ScrollOptions {
  /** Pixels of breathing room left above the target, e.g. for a sticky header. */
  offset?: number
  /** Total travel time in ms. Deliberately slow so the move reads as guidance. */
  duration?: number
}

/**
 * Eased scroll to an element.
 *
 * The browser's native `behavior: 'smooth'` is fast and fixed; this exists so an
 * automatic, un-requested scroll can be slow enough that the visitor sees where
 * they are being taken instead of the page appearing to jump.
 *
 * Two safeguards: `prefers-reduced-motion` skips the animation entirely, and any
 * touch, wheel, or key input cancels it, so the visitor is never fighting the
 * page for control of the scroll position.
 */
export function slowScrollToElement(
  element: HTMLElement,
  { offset = 0, duration = 900 }: ScrollOptions = {},
): void {
  const start = window.scrollY
  const target = element.getBoundingClientRect().top + start - offset
  const distance = target - start
  if (Math.abs(distance) < 8) return

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.scrollTo(0, target)
    return
  }

  let cancelled = false
  const cancel = () => {
    cancelled = true
  }
  const events: (keyof WindowEventMap)[] = [
    'wheel',
    'touchstart',
    'keydown',
    'pointerdown',
  ]
  events.forEach((event) =>
    window.addEventListener(event, cancel, { passive: true, once: true }),
  )
  const cleanUp = () =>
    events.forEach((event) => window.removeEventListener(event, cancel))

  let startTime: number | null = null
  // easeOutCubic: leaves quickly, settles gently onto the target.
  const ease = (t: number) => 1 - Math.pow(1 - t, 3)

  const step = (now: number) => {
    if (cancelled) {
      cleanUp()
      return
    }
    if (startTime === null) startTime = now
    const progress = Math.min((now - startTime) / duration, 1)
    window.scrollTo(0, start + distance * ease(progress))
    if (progress < 1) {
      requestAnimationFrame(step)
    } else {
      cleanUp()
    }
  }
  requestAnimationFrame(step)
}
